import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sheet } from 'react-modal-sheet';
import '../../pages/discover-sheet.css';

interface AddContentButtonProps {
  className?: string;
  onFileUpload?: (file: File) => void;
  disabled?: boolean;
}

const AddContentButton: React.FC<AddContentButtonProps> = ({ 
  className, 
  onFileUpload, 
  disabled = false 
}) => {
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  
  const handleButtonClick = () => {
    setIsBottomSheetOpen(true);
  };
  
  const handleSheetClose = () => {
    setIsBottomSheetOpen(false);
  };
  
  const handleTakePhoto = () => {
    handleSheetClose();
    // Navigate after the animation completes
    setTimeout(() => {
      navigate('/take-photo');
    }, 200);
  };
  
  const handleChooseFromGallery = () => {
    handleSheetClose();
    // Trigger file input after animation completes
    setTimeout(() => {
      fileInputRef.current?.click();
    }, 200);
  };
  
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      console.log("File selected:", file.name);
      if (onFileUpload) {
        onFileUpload(file);
      }
    }
  };

  return (
    <>
      {/* ADD CONTENT button */}
      <button 
        id="add-content-button"
        data-testid="add-content-button"
        aria-label="Add Content"
        onClick={handleButtonClick}
        disabled={disabled}
        className={`text-white rounded-[12px] p-3 focus:outline-none transform transition-transform border-0 ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'
        } ${className || ''}`}
        style={{
          background: 'linear-gradient(187deg, #EC0004 2.32%, #FFB84D 97.68%)',
          width: '48px',
          height: '36px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M5 12H19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 5V19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      
      {/* Hidden file input */}
      <input 
        type="file" 
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/jpeg,image/png,image/webp,image/heic,image/gif"
        disabled={disabled}
        className="hidden"
      />
      
      {/* Bottom Sheet using react-modal-sheet */}
      <Sheet
        isOpen={isBottomSheetOpen}
        onClose={handleSheetClose}
        snapPoints={[300, 0]}
        initialSnap={0}
        style={{
          maxWidth: '640px',
          margin: '0 auto',
          zIndex: 50
        }}
        className="bouncing-sheet"
      >
        <Sheet.Container style={{ backgroundColor: '#151515' }}>
          <Sheet.Header />
          <Sheet.Content>
            <div className="p-4 pb-10">
              <h4 className="text-center font-medium text-lg mb-6 text-white">Add Content</h4>
              <div className="w-full px-4 flex flex-col items-center gap-5">
                <button 
                  onClick={handleTakePhoto}
                  className="btn btn-primary w-full max-w-[280px] flex items-center justify-center gap-3 whitespace-nowrap"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" className="flex-shrink-0 stroke-[1.5]" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ strokeWidth: 2 }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="text-lg">Take a photo</span>
                </button>
                
                <button 
                  onClick={handleChooseFromGallery}
                  className="btn btn-secondary w-full max-w-[280px] flex items-center justify-center gap-3 whitespace-nowrap"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" className="flex-shrink-0 stroke-[1.5]" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ strokeWidth: 2 }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="text-lg">Choose from gallery</span>
                </button>
              </div>
            </div>
          </Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop onTap={handleSheetClose} />
      </Sheet>
    </>
  );
};

export default AddContentButton; 